import React from 'react';
import { Button, Typography, Row, Col, Card, Divider } from 'antd';
import { ArrowRightOutlined, LinkedinOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Home.module.css';
import PagesWrapper from '../../PagesWrapper';
import HeatCraftHPLogo from '../../assets/HeatCraftHPLogoTransparent.png';
import HPInstallerImage from '../../assets/HP_Installer.jpg';
import LIFELogo from './LIFE_logo.png';

const { Title, Text } = Typography;
const { Meta } = Card;

// Training curriculum icon with structured learning path and resources
const TrainingCurriculumIcon = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="curriculumBoard" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f7fbff" />
        <stop offset="100%" stopColor="#e6f4ff" />
      </linearGradient>
    </defs>

    {/* Main board */}
    <rect x="10" y="10" width="60" height="60" rx="8" fill="url(#curriculumBoard)" stroke="#1890ff" strokeWidth="3" />

    {/* Header strip */}
    <rect x="14" y="14" width="52" height="10" rx="3" fill="#1890ff" />
    <rect x="18" y="17" width="18" height="4" rx="2" fill="#ffffff" opacity="0.95" />
    <circle cx="58" cy="19" r="2" fill="#ffffff" opacity="0.9" />

    {/* Curriculum path line */}
    <path d="M 24 32 C 30 28, 36 28, 40 34 C 44 40, 50 40, 56 34" fill="none" stroke="#91d5ff" strokeWidth="3" strokeLinecap="round" />

    {/* Milestones */}
    <circle cx="24" cy="32" r="5" fill="#ffffff" stroke="#52c41a" strokeWidth="2" />
    <path d="M 21.5 32 L 23.5 34 L 27 29.5" fill="none" stroke="#52c41a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

    <circle cx="40" cy="34" r="5" fill="#fff7e6" stroke="#faad14" strokeWidth="2" />
    <rect x="37.5" y="31.5" width="5" height="5" rx="1" fill="#faad14" />

    <circle cx="56" cy="34" r="5" fill="#fff1f0" stroke="#ff4d4f" strokeWidth="2" />
    <path d="M 53.5 36.5 L 58.5 31.5 M 53.5 31.5 L 58.5 36.5" stroke="#ff4d4f" strokeWidth="1.8" strokeLinecap="round" />

    {/* Resource cards */}
    <rect x="16" y="46" width="14" height="14" rx="3" fill="#ffffff" stroke="#91d5ff" strokeWidth="1.5" />
    <path d="M 19 50 L 27 50 M 19 54 L 27 54 M 19 58 L 24 58" stroke="#1890ff" strokeWidth="1.6" strokeLinecap="round" />

    <rect x="33" y="46" width="14" height="14" rx="3" fill="#ffffff" stroke="#91d5ff" strokeWidth="1.5" />
    <path d="M 36 50 L 44 50 L 44 56 L 36 56 Z" fill="#e6f4ff" stroke="#1890ff" strokeWidth="1.4" />
    <path d="M 36 56 L 40 53.5 L 44 56" fill="none" stroke="#52c41a" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />

    <rect x="50" y="46" width="14" height="14" rx="3" fill="#ffffff" stroke="#91d5ff" strokeWidth="1.5" />
    <circle cx="57" cy="53" r="3.2" fill="#f0f7ff" stroke="#1890ff" strokeWidth="1.4" />
    <path d="M 55.4 53 L 56.6 54.3 L 58.9 51.8" fill="none" stroke="#1890ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Toolbox Icon for workplace learning
const ToolboxIcon = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
    {/* Main toolbox body */}
    <rect x="12" y="32" width="56" height="36" rx="4" fill="#e6f4ff" stroke="#1890ff" strokeWidth="3"/>
    {/* Top lid */}
    <rect x="10" y="24" width="60" height="12" rx="3" fill="#1890ff"/>
    {/* Handle */}
    <path d="M 32 24 L 32 18 Q 32 12 40 12 Q 48 12 48 18 L 48 24" 
          stroke="#0050b3" strokeWidth="3" fill="none" strokeLinecap="round"/>
    {/* Handle grip */}
    <rect x="34" y="14" width="12" height="6" rx="2" fill="#0050b3"/>
    {/* Compartments */}
    <rect x="18" y="38" width="16" height="8" rx="2" fill="#fff" stroke="#91d5ff" strokeWidth="1.5"/>
    <rect x="46" y="38" width="16" height="8" rx="2" fill="#fff" stroke="#91d5ff" strokeWidth="1.5"/>
    {/* Wrench */}
    <g transform="translate(20, 50)">
      <rect x="0" y="4" width="14" height="3" rx="1" fill="#52c41a"/>
      <circle cx="1.5" cy="5.5" r="3" fill="none" stroke="#52c41a" strokeWidth="2"/>
      <circle cx="12.5" cy="5.5" r="4" fill="none" stroke="#52c41a" strokeWidth="2"/>
    </g>
    {/* Screwdriver */}
    <g transform="translate(44, 50)">
      <rect x="0" y="5" width="12" height="2" rx="1" fill="#faad14"/>
      <path d="M 12 5 L 16 3 L 16 8 L 12 6" fill="#fa8c16"/>
    </g>
    {/* Ruler markings */}
    <line x1="16" y1="60" x2="64" y2="60" stroke="#91d5ff" strokeWidth="1.5"/>
    <line x1="20" y1="58" x2="20" y2="62" stroke="#91d5ff" strokeWidth="1"/>
    <line x1="28" y1="58" x2="28" y2="62" stroke="#91d5ff" strokeWidth="1"/>
    <line x1="36" y1="58" x2="36" y2="62" stroke="#91d5ff" strokeWidth="1"/>
    <line x1="44" y1="58" x2="44" y2="62" stroke="#91d5ff" strokeWidth="1"/>
    <line x1="52" y1="58" x2="52" y2="62" stroke="#91d5ff" strokeWidth="1"/>
    <line x1="60" y1="58" x2="60" y2="62" stroke="#91d5ff" strokeWidth="1"/>
    {/* Lock clasp */}
    <rect x="38" y="30" width="4" height="6" rx="1" fill="#0050b3"/>
  </svg>
);

// Heat Pump Icon with simulation elements
const HeatPumpIcon = () => (
  <svg width="140" height="80" viewBox="-40 0 160 80" xmlns="http://www.w3.org/2000/svg">
    <style>
      {`
        @keyframes slowSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .slow-fan { 
          transform-origin: 40px 30px;
          animation: slowSpin 6s linear infinite;
        }
      `}
    </style>
    {/* Unit body with shadow */}
    <rect x="10" y="8" width="60" height="64" rx="4" fill="#e6f4ff" stroke="#1890ff" strokeWidth="3"/>
    {/* Top grille section */}
    <rect x="14" y="12" width="52" height="36" rx="3" fill="#fff" stroke="#91d5ff" strokeWidth="2"/>
    {/* Horizontal air grilles */}
    <line x1="18" y1="18" x2="62" y2="18" stroke="#1890ff" strokeWidth="2" strokeLinecap="round"/>
    <line x1="18" y1="24" x2="62" y2="24" stroke="#1890ff" strokeWidth="2" strokeLinecap="round"/>
    <line x1="18" y1="30" x2="62" y2="30" stroke="#1890ff" strokeWidth="2" strokeLinecap="round"/>
    <line x1="18" y1="36" x2="62" y2="36" stroke="#1890ff" strokeWidth="2" strokeLinecap="round"/>
    <line x1="18" y1="42" x2="62" y2="42" stroke="#1890ff" strokeWidth="2" strokeLinecap="round"/>
    {/* Fan behind grille - subtle, slowly rotating */}
    <g className="slow-fan" opacity="0.3">
      <ellipse cx="40" cy="20" rx="2.5" ry="8" fill="#0050b3"/>
      <ellipse cx="40" cy="40" rx="2.5" ry="8" fill="#0050b3"/>
      <ellipse cx="30" cy="30" rx="8" ry="2.5" fill="#0050b3"/>
      <ellipse cx="50" cy="30" rx="8" ry="2.5" fill="#0050b3"/>
    </g>
    {/* Control panel section */}
    <rect x="14" y="50" width="52" height="18" rx="2" fill="#f0f7ff" stroke="#91d5ff" strokeWidth="1.5"/>
    {/* Pipes on sides */}
    <path d="M 10 20 L 4 20 L 4 40 L 10 40" stroke="#40a9ff" strokeWidth="3" fill="none"/>
    <path d="M 70 20 L 76 20 L 76 40 L 70 40" stroke="#ff4d4f" strokeWidth="3" fill="none"/>
    {/* Connection points */}
    <circle cx="4" cy="30" r="3" fill="#40a9ff"/>
    <circle cx="76" cy="30" r="3" fill="#ff4d4f"/>
    
    {/* Heat flux / Numbers panel - left side, outside unit */}
    <g opacity="0.9">
      <rect x="-34" y="22" width="32" height="20" rx="2" fill="#fff" stroke="#ff4d4f" strokeWidth="1.5"/>
      {/* Title */}
      <text x="-18" y="27" fontSize="3.5" fill="#ff4d4f" textAnchor="middle" fontWeight="bold">HEAT</text>
      {/* Heat flux bars */}
      <rect x="-31" y="30" width="10" height="2" rx="0.5" fill="#ff7875"/>
      <rect x="-31" y="33" width="17" height="2" rx="0.5" fill="#ffa39e"/>
      <rect x="-31" y="36" width="13" height="2" rx="0.5" fill="#ffccc7"/>
      {/* Values */}
      <text x="-19" y="31.5" fontSize="3" fill="#595959" fontWeight="bold">3.2</text>
      <text x="-12" y="34.5" fontSize="3" fill="#595959" fontWeight="bold">4.8</text>
      <text x="-16" y="37.5" fontSize="3" fill="#595959" fontWeight="bold">3.9</text>
      {/* Unit label */}
      <text x="-8" y="40" fontSize="3" fill="#8c8c8c">kW</text>
    </g>
    
    {/* Performance graph overlay - right side, outside unit */}
    <g opacity="0.9">
      <rect x="82" y="22" width="32" height="20" rx="2" fill="#fff" stroke="#1890ff" strokeWidth="1.5"/>
      {/* Title */}
      <text x="98" y="27" fontSize="3.5" fill="#1890ff" textAnchor="middle" fontWeight="bold">COP</text>
      {/* Grid lines */}
      <line x1="84" y1="30" x2="112" y2="30" stroke="#f0f0f0" strokeWidth="0.5"/>
      <line x1="84" y1="33" x2="112" y2="33" stroke="#f0f0f0" strokeWidth="0.5"/>
      <line x1="84" y1="36" x2="112" y2="36" stroke="#f0f0f0" strokeWidth="0.5"/>
      <line x1="84" y1="39" x2="112" y2="39" stroke="#f0f0f0" strokeWidth="0.5"/>
      {/* Y-axis */}
      <line x1="84" y1="29" x2="84" y2="40" stroke="#d9d9d9" strokeWidth="1"/>
      {/* X-axis */}
      <line x1="84" y1="40" x2="112" y2="40" stroke="#d9d9d9" strokeWidth="1"/>
      {/* Graph line showing COP curve */}
      <polyline 
        points="84,38 88,36 92,33 96,31 100,30 104,30 108,31 112,32" 
        fill="none" 
        stroke="#52c41a" 
        strokeWidth="2"
      />
      {/* Data points */}
      <circle cx="96" cy="31" r="1" fill="#52c41a"/>
      <circle cx="104" cy="30" r="1" fill="#52c41a"/>
    </g>
    
    {/* Temperature indicators - small gauges */}
    <g opacity="0.85">
      {/* Hot side indicator */}
      <circle cx="60" cy="22" r="3.5" fill="#ff7875" stroke="#ff4d4f" strokeWidth="1"/>
      <text x="60" y="23.5" fontSize="5" fill="#fff" textAnchor="middle" fontWeight="bold">H</text>
      {/* Cold side indicator */}
      <circle cx="20" cy="22" r="3.5" fill="#91d5ff" stroke="#40a9ff" strokeWidth="1"/>
      <text x="20" y="23.5" fontSize="5" fill="#fff" textAnchor="middle" fontWeight="bold">C</text>
    </g>
    {/* Flow direction arrows */}
    <g opacity="0.7">
      <path d="M 8 24 L 6 26 L 8 28" fill="none" stroke="#40a9ff" strokeWidth="1.5"/>
      <path d="M 72 36 L 74 34 L 72 32" fill="none" stroke="#ff4d4f" strokeWidth="1.5"/>
    </g>
    {/* Indicator lights */}
    <circle cx="20" cy="68" r="2" fill="#52c41a"/>
    <circle cx="28" cy="68" r="2" fill="#faad14"/>
  </svg>
);

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      title: t('home.features.azCourse.title'),
      description: t('home.features.azCourse.description'),
      icon: <TrainingCurriculumIcon />,
      link: '/training-curriculum'
    },
    {
      title: t('home.features.simulations.title'),
      description: t('home.features.simulations.description'),
      icon: <HeatPumpIcon />,
      link: '/simulations'
    },{
      title: t('home.features.workplaceLearning.title'),
      description: t('home.features.workplaceLearning.description'),
      icon: <ToolboxIcon />
    }
  ];

  // Energy wave background
  const EnergyWaves = () => (
    <svg className={styles.energyWaves} viewBox="0 0 1440 320" preserveAspectRatio="none">
      <path
        className={styles.wave1}
        d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
      />
      <path
        className={styles.wave2}
        d="M0,256L48,240C96,224,192,192,288,181.3C384,171,480,181,576,208C672,235,768,277,864,277.3C960,277,1056,235,1152,208C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
      />
    </svg>
  );

  return (
    <PagesWrapper>
      <div className={styles.homeContainer}>
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroGrid}>
              <div className={styles.heroTextWrapper}>
                <Title level={1} className={styles.heroTitle}>
                  {t('home.welcome')}
                </Title>
                <Title level={3} className={styles.heroTagline}>
                  {t('home.tagline')}
                </Title>
                <Text className={styles.heroDescription}>
                    {t('home.description')}
                </Text>
                <div className={styles.heroKeyPoints}>
                  <div className={styles.keyPoint}>{t('home.keyPoints.learnScience')}</div>
                  <div className={styles.keyPoint}>{t('home.keyPoints.masterCraft')}</div>
                  <div className={styles.keyPoint}>{t('home.keyPoints.buildBetter')}</div>
                </div>
                <div className={styles.ctaButtons}>
                  <Button 
                    type="primary" 
                    size="large" 
                    className={styles.primaryButton}
                    onClick={() => navigate('/simulations')}
                  >
                    {t('home.getStarted')} <ArrowRightOutlined />
                  </Button>
                </div>
              </div>
              <div className={styles.heroVisual}>
                <div className={styles.heroImageWrapper}>
                  <img 
                    src={HPInstallerImage} 
                    alt="Heat pump installer" 
                    className={styles.heroImage}
                  />
                </div>
              </div>
            </div>
          </div>
          <EnergyWaves />
        </section>

        <section className={styles.featuresSection}>
          <Divider />
          <Row gutter={[24, 24]} className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} md={6} lg={6} key={index}>
                <Card 
                  hoverable 
                  className={styles.featureCard}
                  cover={<div className={styles.featureIcon}>{feature.icon}</div>}
                  onClick={() => feature.link && navigate(feature.link)}
                  style={{ cursor: feature.link ? 'pointer' : 'default' }}
                >
                  <Meta
                    title={<Title level={5} className={styles.featureTitle}>{feature.title}</Title>}
                    description={<Text className={styles.featureText}>{feature.description}</Text>}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/*<section className={styles.programLevelsSection}>
          <div className={styles.programLevelsHeader}>
            <Title level={2} className={styles.programLevelsTitle}>
              {t('home.programLevels.sectionTitle')}
            </Title>
            <Text className={styles.programLevelsSubtitle}>
              {t('home.programLevels.sectionSubtitle')}
            </Text>
          </div>
          <div className={styles.levelsGrid}>
            {[1,2,3,4].map((levelNum) => (
              <div key={levelNum} className={styles.levelCard}>
                <div className={styles.levelCardHeader}>
                  <div className={styles.levelBadge}>{levelNum}</div>
                  <Title level={4} className={styles.levelTitle}>
                    {t(`home.programLevels.level${levelNum}.title`)}
                  </Title>
                </div>
                <div className={styles.conceptsLabel}>
                  {t('home.programLevels.conceptsLabel')}
                </div>
                <ul className={styles.conceptsList}>
                  {t(`home.programLevels.level${levelNum}.concepts`, { returnObjects: true }).map((concept, idx) => (
                    <li key={idx} className={styles.conceptItem}>{concept}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>*/}

        <footer className={styles.footerSection}>
          <div className={styles.footerContent}>
            <div className={styles.footerTop}>
              <div className={styles.footerLogoWrapper}>
                <img src={HeatCraftHPLogo} alt="HeatCraftHP Logo" className={styles.footerLogoImage} />
                <Title level={5} className={styles.footerBrandName}>HeatCraftHP</Title>
                <Text className={styles.footerBrandSub}>{t('footer.euProgram')}</Text>
              </div>
              <div className={styles.footerInfoRow}>
                <div className={styles.footerInfoBlock}>
                  <Text className={styles.footerInfoLabel}>{t('footer.projectCoordinator')}</Text>
                  <Text className={styles.footerInfoValue}>University of Twente</Text>
                  <Text className={styles.footerInfoDetail}>Netherlands</Text>
                </div>
                <div className={styles.footerInfoBlock}>
                  <Text className={styles.footerInfoLabel}>{t('footer.dissemination')}</Text>
                  <Text className={styles.footerInfoValue}>European Heat Pump Association (EHPA)</Text>
                  <Text className={styles.footerInfoDetail}>Belgium</Text>
                </div>
              </div>
            </div>
            <div className={styles.footerDividerLine} />
            <div className={styles.footerDisclaimer}>
              <img
                src={LIFELogo}
                alt="Co-funded by the European Union"
                className={styles.footerDisclaimerImage}
              />
              <Text className={styles.footerDisclaimerText}>
                Co-funded by the European Union. Views and opinions expressed are however those of the
                author(s) only and do not necessarily reflect those of the European Union or CINEA.
                Neither the European Union nor the granting authority can be held responsible for them.
              </Text>
            </div>
            <div className={styles.footerDividerLine} />
            <div className={styles.footerBottom}>
              <Text className={styles.footerCopyright}>{t('footer.copyright', { year: new Date().getFullYear() })}</Text>
              <span className={styles.footerSeparator}>|</span>
              <Link to="/terms" className={styles.footerLink}>
                {t('footer.terms', 'Terms & Conditions')}
              </Link>
              <span className={styles.footerSeparator}>|</span>
              <Link to="/contact" className={styles.footerLink}>
                {t('footer.contact', 'Contact')}
              </Link>
              <div className={styles.footerSocials}>
                <a href="https://linkedin.com/company/heatcrafthp-project/" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon} aria-label="LinkedIn">
                  <LinkedinOutlined />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </PagesWrapper>
  );
};

export default Home;
